import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  Stack,
  Box,
} from "@mui/material";
import {
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  Link,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useTemplateSettings from "../../hooks/useTemplateSettings";
import MuPb from "../../widgets/MuPb";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getAuthHeader,
  getDbHeader,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import ImageCropperModal from "../../widgets/ImageCropperModal";
import { API_ENDPOINT, APP_ID } from "../../utils/constants";
import { toast } from "react-toastify";
import FullImageModal from "../../widgets/FullImageModal";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import { CircularProgressbar } from "react-circular-progressbar";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { SketchPicker } from "react-color";

const defaultFooterLinks = {
  fb: "",
  in: "",
  x: "",
  ln: "",
  siteName: "",
  siteLink: "",
};

const TemplateSettings = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useTemplateSettings(id);
  const [showImage, setShowImage] = useState(false);
  const [imageToShow, setImageToShow] = useState("");
  const [config, setConfig] = useState({
    name: "",
    formTitle: "",
    headerBanner: "",
    headerBg: "",
    headerSliders: [],
    footerBanner: "",
    footerLogo: "",
    footerLinks: defaultFooterLinks,
    footerBgColor: "#ffffff",
    submitBgColor: "#1976d2",
  });
  const [cropperOpen, setCropperOpen] = useState(false);
  const [propertyType, setPropertyType] = useState("");
  const [aspectRatio, setAspectRatio] = useState(2 / 1);

  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingSpecifics, setSubmittingSpecifics] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [submitColorPickerOpen, setSubmitColorPickerOpen] = useState(false);

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    refetch();
  };

  const handleCloseImage = () => {
    setShowImage(false);
  };

  const handleShowImage = (imgUrl) => {
    setImageToShow(imgUrl);
    setShowImage(true);
  };

  // Sync local state when data loads
  useEffect(() => {
    if (data?.properties) {
      // Build a clean footerLinks object with only the expected keys
      const socialKeys = ["fb", "in", "x", "ln", "siteName", "siteLink"];
      const loadedFooterLinks = { ...defaultFooterLinks };
      if (data.properties.footerLinks) {
        socialKeys.forEach((key) => {
          if (data.properties.footerLinks[key] !== undefined) {
            loadedFooterLinks[key] = data.properties.footerLinks[key];
          }
        });
      }

      // Extract footerBgColor
      const loadedFooterBgColor =
        data.properties.footerBgColor ||
        (data.properties.footerLinks &&
          data.properties.footerLinks.footerBgColor) ||
        "#ffffff";

      // Extract submitBgColor
      const loadedSubmitBgColor = data.properties.submitBgColor || "#1976d2";

      setConfig({
        name: data.name || "",
        formTitle: data.formTitle || "",
        headerBanner: data.properties.headerBanner || "",
        headerBg: data.properties.headerBg || "",
        headerSliders: data.properties.headerSliders || [],
        footerBanner: data.properties.footerBanner || "",
        footerLogo: data.properties.footerLogo || "",
        footerLinks: loadedFooterLinks,
        footerBgColor: loadedFooterBgColor,
        submitBgColor: loadedSubmitBgColor,
      });
    }
  }, [data]);

  const handleChange = (path, value) => {
    const paths = path.split(".");
    setConfig((prev) => ({
      ...prev,
      [paths[0]]:
        paths.length === 1
          ? value
          : {
              ...prev[paths[0]],
              [paths[1]]: value,
            },
    }));
  };

  const handleSliderChange = (index, value) => {
    const newSliders = [...config.headerSliders];
    newSliders[index] = value;
    setConfig((prev) => ({ ...prev, headerSliders: newSliders }));
  };

  const addSlider = () => {
    setConfig((prev) => ({
      ...prev,
      headerSliders: [...prev.headerSliders, ""],
    }));
  };

  const removeSlider = (index) => {
    setConfig((prev) => ({
      ...prev,
      headerSliders: prev.headerSliders.filter((_, i) => i !== index),
    }));
  };

  const handleSubmitSpecifics = async () => {
    try {
      setSubmittingSpecifics(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}event/update-template-specifics`,
        {
          formTitle: config.formTitle,
        },
        getAuthHeader(id)
      );
      toast.success(data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmittingSpecifics(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}event/submit-footer-link`,
        {
          footerLinks: config.footerLinks,
          footerBgColor: config.footerBgColor,
          submitBgColor: config.submitBgColor,
        },
        getAuthHeader(id)
      );
      toast.success(data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <SimpleYesNoDialog
        title={"Please Confirm"}
        message={"Are you sure you want to delete this?"}
        onYes={async () => {
          try {
            setDeleting(true);
            setDeleteOpen(false);
            const { data } = await axios.delete(
              `${API_ENDPOINT}event/delete-slider-image?imgUrl=${deleteId}`,
              getDbHeader(id)
            );
            toast.success(data.message);
            refetch();
          } catch (error) {
            toastError(error);
          } finally {
            setDeleting(false);
          }
        }}
        show={deleteOpen}
        setShow={setDeleteOpen}
      />
      <FullImageModal
        imgUrl={imageToShow}
        open={showImage}
        handleClose={handleCloseImage}
      />
      <ImageCropperModal
        open={cropperOpen}
        type={propertyType}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}event/upload-image-event-settings/${propertyType}${
          propertyType === "properties.headerSliders" ? "?qp=sliders" : ""
        }`}
        headers={{
          dbauth: id,
        }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />
      <Typography variant="h5" gutterBottom>
        {config.name}
      </Typography>

      {/* ── Form Details ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Form Details
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Form Title"
              value={config.formTitle}
              onChange={(e) => handleChange("formTitle", e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction={"row"}>
          <Box flex={1} />
          <LoadingButton
            loading={submittingSpecifics}
            sx={{ mt: 2 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitSpecifics}
            startIcon={<SaveOutlinedIcon />}
          >
            Save
          </LoadingButton>
        </Stack>
      </Paper>

      {/* ── Header Settings ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Header Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction={"row"} gap={1}>
              <TextField
                size="small"
                fullWidth
                disabled
                label="Header Banner URL"
                value={config.headerBanner}
                onChange={(e) => handleChange("headerBanner", e.target.value)}
              />
              <IconButton
                onClick={() => handleShowImage(config.headerBanner)}
                size="small"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => {
                  setPropertyType("properties.headerBanner");
                  setAspectRatio(1198 / 199);
                  setCropperOpen(true);
                }}
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
              >
                Change
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction={"row"} gap={1}>
              <TextField
                size="small"
                fullWidth
                disabled
                label="Header Background URL"
                value={config.headerBg}
                onChange={(e) => handleChange("headerBg", e.target.value)}
              />
              <IconButton
                onClick={() => handleShowImage(config.headerBg)}
                size="small"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => {
                  setPropertyType("properties.headerBg");
                  setAspectRatio(1921 / 623);
                  setCropperOpen(true);
                }}
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
              >
                Change
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Header Sliders
            </Typography>
            {config.headerSliders.map((slider, index) => (
              <div
                key={index}
                style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
              >
                <TextField
                  size="small"
                  fullWidth
                  disabled
                  label={`Slider ${index + 1} URL`}
                  value={slider}
                  onChange={(e) => handleSliderChange(index, e.target.value)}
                />
                <IconButton
                  onClick={() => handleShowImage(slider)}
                  size="small"
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    setDeleteOpen(true);
                    setDeleteId(slider);
                  }}
                  disabled={config.headerSliders.length <= 1}
                >
                  {deleting ? (
                    <CircularProgressbar />
                  ) : (
                    <DeleteOutlineIcon fontSize="small" />
                  )}
                </IconButton>
              </div>
            ))}
            <Button
              onClick={() => {
                setPropertyType("properties.headerSliders");
                setAspectRatio(1201 / 301);
                setCropperOpen(true);
              }}
              color="primary"
            >
              Add Slider
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Footer Settings ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Footer Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction={"row"} gap={1}>
              <TextField
                size="small"
                fullWidth
                disabled
                label="Footer Banner URL"
                value={config.footerBanner}
                onChange={(e) => handleChange("footerBanner", e.target.value)}
              />
              <IconButton
                onClick={() => handleShowImage(config.footerBanner)}
                size="small"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => {
                  setPropertyType("properties.footerBanner");
                  setAspectRatio(601 / 121);
                  setCropperOpen(true);
                }}
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
              >
                Change
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction={"row"} gap={1}>
              <TextField
                size="small"
                fullWidth
                disabled
                label="Footer Logo URL"
                value={config.footerLogo}
                onChange={(e) => handleChange("footerLogo", e.target.value)}
              />
              <IconButton
                onClick={() => handleShowImage(config.footerLogo)}
                size="small"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
              <Button
                onClick={() => {
                  setPropertyType("properties.footerLogo");
                  setAspectRatio(425 / 90);
                  setCropperOpen(true);
                }}
                variant="outlined"
                startIcon={<AddIcon />}
                size="small"
              >
                Change
              </Button>
            </Stack>
          </Grid>

          {/* ── Footer Background Color ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Footer Background Color
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                onClick={() => setColorPickerOpen((prev) => !prev)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: "1px solid #ccc",
                  backgroundColor: config.footerBgColor,
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  transition: "transform 0.15s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
              <TextField
                size="small"
                label="Hex Color Code"
                value={config.footerBgColor}
                onChange={(e) => handleChange("footerBgColor", e.target.value)}
                sx={{ width: 180 }}
                inputProps={{ maxLength: 7 }}
              />
            </Stack>

            {colorPickerOpen && (
              <Box sx={{ mt: 1, position: "relative", zIndex: 10 }}>
                <Box
                  onClick={() => setColorPickerOpen(false)}
                  sx={{ position: "fixed", inset: 0 }}
                />
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <SketchPicker
                    color={config.footerBgColor}
                    onChange={(color) =>
                      handleChange("footerBgColor", color.hex)
                    }
                  />
                </Box>
              </Box>
            )}
          </Grid>

          {/* ── Submit Button Color ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Submit Button Color
            </Typography>
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                onClick={() => setSubmitColorPickerOpen((prev) => !prev)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: "1px solid #ccc",
                  backgroundColor: config.submitBgColor,
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  transition: "transform 0.15s",
                  "&:hover": { transform: "scale(1.1)" },
                }}
              />
              <TextField
                size="small"
                label="Hex Color Code"
                value={config.submitBgColor}
                onChange={(e) => handleChange("submitBgColor", e.target.value)}
                sx={{ width: 180 }}
                inputProps={{ maxLength: 7 }}
              />
            </Stack>

            {submitColorPickerOpen && (
              <Box sx={{ mt: 1, position: "relative", zIndex: 10 }}>
                <Box
                  onClick={() => setSubmitColorPickerOpen(false)}
                  sx={{ position: "fixed", inset: 0 }}
                />
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <SketchPicker
                    color={config.submitBgColor}
                    onChange={(color) =>
                      handleChange("submitBgColor", color.hex)
                    }
                  />
                </Box>
              </Box>
            )}
          </Grid>

          {/* ── Social Links ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Social Links
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Facebook URL"
                  value={config.footerLinks.fb}
                  onChange={(e) =>
                    handleChange("footerLinks.fb", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Facebook />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Instagram URL"
                  value={config.footerLinks.in}
                  onChange={(e) =>
                    handleChange("footerLinks.in", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Instagram />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Twitter (X) URL"
                  value={config.footerLinks.x}
                  onChange={(e) =>
                    handleChange("footerLinks.x", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Twitter />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="LinkedIn URL"
                  value={config.footerLinks.ln}
                  onChange={(e) =>
                    handleChange("footerLinks.ln", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkedIn />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Site Name"
                  value={config.footerLinks.siteName}
                  onChange={(e) =>
                    handleChange("footerLinks.siteName", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Link />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Site URL"
                  value={config.footerLinks.siteLink}
                  onChange={(e) =>
                    handleChange("footerLinks.siteLink", e.target.value)
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Link />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Stack direction={"row"}>
          <Box flex={1} />
          <LoadingButton
            loading={submitting}
            sx={{ mt: 2 }}
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
            startIcon={<SaveOutlinedIcon />}
          >
            Save
          </LoadingButton>
        </Stack>
      </Paper>
    </Container>
  );
};

export default TemplateSettings;
